import { Box, Flex, Icon, Stack, Text, VStack } from "@chakra-ui/react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { FiMessageSquare } from "react-icons/fi";
import {
  PortalLifecycleStatus,
  PortalSourcingEventDetailDto,
  SeQuestionKind,
  useGetPortalSupplierInformationQuery,
  useGetPortalMyBidDocumentsQuery,
  useGetPortalSeQuestionsQuery,
} from "../../../../../autogen/bff-api";
import { AnnouncementCard } from "../../../../../common/components/communication/AnnouncementCard";
import { useApiError } from "../../../../../common/errors/useApiError";
import { useAppSelector } from "../../../../../common/redux/hooks";
import { SectionCard } from "../SectionCard";
import { SupplierAskQuestionForm } from "./SupplierAskQuestionForm";
import { SupplierClarificationThread } from "./SupplierClarificationThread";
import { SupplierQuestionThread } from "./SupplierQuestionThread";
import { useOpenPortalRound } from "../useOpenPortalRound";
import { isDesignContest } from "../../../buyer/wizard/utils/procedure";

const threadAnchorId = (questionId: string) => `question-${questionId}`;

export const EventCommunicationSection = ({
  event,
}: {
  event: PortalSourcingEventDetailDto;
}) => {
  const { t } = useTranslation();
  const displayer = useApiError();
  const eventId = event.id;
  const designContest = isDesignContest(event.procedureType);
  const [searchParams] = useSearchParams();
  const authState = useAppSelector((state) => state.authState.state);
  const isLoggedIn = authState?.type === "LoggedInWithOrgContext";
  const { round: openRound, isLoading: isLoadingRounds } = useOpenPortalRound(event);

  const { data: announcementsData, error: announcementsError } =
    useGetPortalSupplierInformationQuery({ eventId }, { skip: !isLoggedIn });
  const { data: questionsData, error: questionsError } =
    useGetPortalSeQuestionsQuery({ eventId }, { skip: !isLoggedIn });
  const { data: myBidDocuments } = useGetPortalMyBidDocumentsQuery({ eventId }, { skip: !isLoggedIn });

  useEffect(() => {
    if (announcementsError) displayer.triggerSoft(announcementsError);
  }, [announcementsError, displayer]);

  useEffect(() => {
    if (questionsError) displayer.triggerSoft(questionsError);
  }, [questionsError, displayer]);

  const announcements = announcementsData?.supplierInformation ?? [];
  const threads = questionsData?.questions ?? [];
  // The buyer may open a private clarification dialog with this tenderer after the deadline (FOA § 23-5). It arrives
  // on the same endpoint as the tenderer's own questions, but it is a two-way thread rather than a Q&A.
  const myQuestions = threads.filter((q) => q.kind === SeQuestionKind.Question);
  const clarifications = threads.filter((q) => q.kind === SeQuestionKind.Clarification);
  const announcementsById = new Map(announcements.map((a) => [a.id, a]));
  const requestDocuments = (event.documents ?? []).map((d) => ({ id: d.id, name: d.name }));
  const offerDocuments = (myBidDocuments?.documents ?? []).map((d) => ({
    id: d.documentId,
    name: d.fileName,
  }));

  // A question the supplier asked that was published already shows as their own thread (with the answer),
  // so suppress the public announcement derived from it — otherwise it reads as a duplicate. Other suppliers,
  // who don't have the thread, still see it.
  const ownPublishedAnnouncementIds = new Set(
    myQuestions
      .map((q) => q.publishedInformationId)
      .filter((id): id is string => !!id),
  );
  const feedAnnouncements = announcements.filter(
    (a) => !ownPublishedAnnouncementIds.has(a.id),
  );
  const count = feedAnnouncements.length + myQuestions.length + clarifications.length;
  const threadCount = myQuestions.length + clarifications.length;
  const linkedQuestionId = searchParams.get("question");
  const scrolledToQuestion = useRef<string | null>(null);
  useEffect(() => {
    if (!linkedQuestionId || scrolledToQuestion.current === linkedQuestionId) return;
    const thread = document.getElementById(threadAnchorId(linkedQuestionId));
    if (!thread) return;
    scrolledToQuestion.current = linkedQuestionId;
    thread.scrollIntoView({ block: "center" });
  }, [linkedQuestionId, threadCount]);

  // One chronological feed (oldest → newest). Each question stays grouped with its answer (the thread is one item),
  // anchored at the time the question was asked.
  const feed = [
    ...feedAnnouncements.map((a) => ({
      at: a.createdAt,
      el: <AnnouncementCard key={`ann-${a.id}`} eventId={eventId} announcement={a} buyerName={event.buyerName} />,
    })),
    ...myQuestions.map((q) => ({
      at: q.createdAt,
      el: (
        <Box key={`q-${q.id}`} id={threadAnchorId(q.id)}>
        <SupplierQuestionThread
          question={q}
          publishedAnswer={q.publishedInformationId ? announcementsById.get(q.publishedInformationId) : undefined}
        />
        </Box>
      ),
    })),
    ...clarifications.map((clarification) => ({
      at: clarification.createdAt,
      el: (
        <Box key={`c-${clarification.id}`} id={threadAnchorId(clarification.id)}>
          <SupplierClarificationThread
            clarification={clarification}
            requestDocuments={requestDocuments}
            offerDocuments={offerDocuments}
            designContest={designContest}
          />
        </Box>
      ),
    })),
  ]
    .sort((x, y) => x.at.localeCompare(y.at))
    .map((item) => item.el);

  return (
    <SectionCard
      icon={FiMessageSquare}
      title={t("Questions and answers")}
      count={count > 0 ? count : undefined}
    >
      <Stack spacing="4">
        {feed}
        {isLoggedIn && count === 0 && (
          <VStack spacing="2" py="4" textAlign="center">
            <Flex
              w="10"
              h="10"
              rounded="lg"
              align="center"
              justify="center"
              bg="smPrimarySofter"
              color="smChipTealFg"
            >
              <Icon as={FiMessageSquare} boxSize="5" />
            </Flex>
            <Text fontSize="sm" fontWeight="semibold" color="smText">
              {t("No questions yet")}
            </Text>
            <Text fontSize="sm" color="smMuted" maxW="sm">
              {designContest
                ? t("Ask the buyer if anything in the contest is unclear. Answers may be published to all participants.")
                : t("Ask the buyer if anything in the tender is unclear. Answers may be published to all tenderers.")}
            </Text>
          </VStack>
        )}
        {isLoggedIn ? (
          <SupplierAskQuestionForm
            eventId={eventId}
            questionDeadline={openRound?.deadline ?? event.questionDeadline ?? null}
            deadlineIsRoundDeadline={openRound != null}
            eventIsOpen={event.lifecycle.status === PortalLifecycleStatus.Open || openRound != null}
            isResolvingOpenRound={isLoadingRounds}
            requestDocuments={requestDocuments}
            offerDocuments={offerDocuments}
            designContest={designContest}
          />
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Log in to ask the buyer a question.")}
          </Text>
        )}
      </Stack>
    </SectionCard>
  );
};
